import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";
import Toast from "@/components/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, PackageOpen, Package, Boxes, Upload, Layers, Search } from "lucide-react";
import { ReceiveDialog, UnannouncedDialog, ItemDialog, AssignDialog, ReceiptsDialog, uploadPhotos } from "@/components/inbound/InboundDialogs";
import ImportResultDialog from "@/pages/admin/ImportResultDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const OPEN_STATES = ["ORDERED", "PARTIAL"];
const STATE_STYLE = {
  ORDERED: "bg-slate-100 text-slate-700", PARTIAL: "bg-amber-100 text-amber-800", COMPLETE: "bg-emerald-100 text-emerald-800",
  CLOSED_SHORT: "bg-slate-200 text-slate-600", UNANNOUNCED: "bg-violet-100 text-violet-800", OWNER_UNKNOWN: "bg-amber-200 text-amber-900",
};
const ROW_STYLE = { COMPLETE: "bg-emerald-50/70", CLOSED_SHORT: "bg-slate-50 text-slate-500", OWNER_UNKNOWN: "bg-amber-50/70" };

/** Pallets / Inbound / Stock switch at the top of the planning and sender pages; same look as the admin tabs. */
export function InboundTabs({ value, onChange, withPallets = true }) {
  const { t } = useLang();
  const tabs = [["pallets", Package], ["inbound", PackageOpen], ["stock", Boxes]].slice(withPallets ? 0 : 1);
  return (
    <Tabs value={value} onValueChange={onChange}>
      <TabsList className="w-full overflow-x-auto flex-nowrap">
        {tabs.map(([v, Icon]) => (
          <TabsTrigger key={v} value={v} data-testid={`page-tab-${v}`} className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm">
            <Icon className="w-3.5 h-3.5" />{t(`inb.tab.${v}`)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function Product({ row, t }) {
  return (
    <div className="min-w-0">
      <p className="truncate font-semibold text-slate-900">{row.product_name || t("inb.unknown_product")}</p>
      <p className="font-mono text-[11px] text-slate-500">
        {row.asin}{" "}
        <a href={`https://www.amazon.co.uk/dp/${encodeURIComponent(row.asin)}`} target="_blank" rel="noreferrer"
          className="text-blue-600 hover:underline">Amazon ↗</a>
      </p>
    </div>
  );
}

/** Inbound goods ("view" = "inbound") or the derived stock ("stock") at a sender warehouse.
 *  side "tenant": own rows, opens and edits order rows. side "sender": every tenant at its warehouse, receives.
 *  side "platform": every tenant at every sender warehouse, read only. */
export default function InboundPanel({ side, view }) {
  const { t } = useLang();
  const tenant = side === "tenant";
  const sender = side === "sender";
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [warehouses, setWarehouses] = useState(null);
  const [wid, setWid] = useState("");
  const [filter, setFilter] = useState("open");
  const [grouped, setGrouped] = useState(false);
  const [q, setQ] = useState("");
  const [tenantId, setTenantId] = useState("all");
  const [rows, setRows] = useState([]);
  const [stock, setStock] = useState([]);
  const [dialog, setDialog] = useState(null); // { kind, row }
  const [msg, setMsg] = useState(null);
  const showMsg = (type, text) => setMsg({ type, text });
  const fail = (e) => showMsg("error", typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("inb.error"));
  const warehouse = warehouses?.find(w => w.id === wid);

  useEffect(() => {
    axios.get(`${API}/inbound/options`, ax)
      .then(({ data }) => { setWarehouses(data.warehouses); setWid(data.warehouses[0]?.id || ""); })
      .catch(e => { setWarehouses([]); fail(e); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (silent = false) => {
    if (!wid) return;
    try {
      if (view === "stock") {
        setStock((await axios.get(`${API}/inbound/stock`, { ...ax, params: { warehouse_id: wid } })).data);
      } else {
        setRows((await axios.get(`${API}/inbound/items`, { ...ax, params: { view: filter, warehouse_id: wid } })).data);
      }
    } catch (e) { if (!silent) fail(e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [wid, filter, view]);
  const { refresh, refreshing } = useAutoRefresh(() => load(true), { paused: !!dialog });

  const done = (text) => { setDialog(null); if (text) showMsg("success", text); load(); };
  const afterReceipt = async (receipt, files, text) => {
    done(text);
    if (files.length && await uploadPhotos(receipt.id, files)) showMsg("error", t("inb.photos_failed"));
  };
  const act = async (call, text) => {
    try { await call(); done(text); } catch (e) { setDialog(null); fail(e); }
  };
  const importRows = async (file) => {
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/inbound/items/import`, form,
        { ...ax, params: { warehouse_id: wid }, headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); load();
    } catch (e) { fail(e); }
    finally { setImporting(false); }
  };

  if (warehouses === null) return <div className="py-12 text-center text-sm text-slate-500">{t("common.loading")}</div>;
  if (!warehouses.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
        <PackageOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
        <p className="font-semibold text-slate-700">{t("inb.no_warehouse")}</p>
      </div>
    );
  }

  const tenantName = (id) => warehouse?.tenants?.find(x => x.id === id)?.name || "";
  const filters = tenant ? ["open", "done"] : ["open", "done", "unknown"];
  // Owner-unknown rows have no tenant, so the tenant filter is off there (disabled, not hidden, so the row does not jump).
  const unknownView = view === "inbound" && filter === "unknown";
  const byTenant = !tenant && !unknownView && tenantId !== "all";
  const needle = q.trim().toLowerCase();
  const match = (r) => (!byTenant || r.tenant_id === tenantId)
    && `${r.asin} ${r.product_name || ""} ${r.order_id || ""}`.toLowerCase().includes(needle);
  const shownStock = needle || byTenant ? stock.filter(match) : stock;
  const shownRows = needle || byTenant ? rows.filter(match) : rows;
  const byOrder = new Map();
  shownRows.forEach(r => byOrder.set(r.order_id || "", [...(byOrder.get(r.order_id || "") || []), r]));

  const renderRow = (row) => {
    const open = OPEN_STATES.includes(row.state);
    return (
      <tr key={row.id} className={`border-t border-slate-100 ${ROW_STYLE[row.state] || ""}`} data-testid={`inbound-row-${row.id}`}>
        <td className="px-3 py-2">
          <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_STYLE[row.state]}`}>
            {t(`inb.state.${row.state}`)}
          </span>
        </td>
        {!tenant && <td className="px-3 py-2 text-xs font-semibold text-slate-600">{row.tenant_name || "—"}</td>}
        <td className="px-3 py-2">
          <Product row={row} t={t} />
          <div className="mt-1 flex flex-wrap gap-1">
            {row.received_damaged > 0 && (
              <button type="button" onClick={() => setDialog({ kind: "receipts", row })}
                className={`rounded-full border px-2 text-[11px] font-bold ${row.unacknowledged_damage
                  ? "border-red-300 bg-red-50 text-red-700" : "border-slate-300 bg-white text-slate-500"}`}>
                {t("inb.damaged_count", { count: row.received_damaged })}
                {!row.unacknowledged_damage && <> · {t("inb.seen")}</>}
              </button>
            )}
            {row.tenant_id && !row.in_list && (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-2 text-[11px] font-bold text-amber-700"
                title={t("inb.not_in_list_title")}>{t("inb.not_in_list")}</span>
            )}
          </div>
        </td>
        <td className="px-3 py-2 font-mono text-xs">{row.order_id || "–"}</td>
        <td className="px-3 py-2 text-right tabular-nums">{row.ordered_quantity ?? "–"}</td>
        <td className="px-3 py-2 text-right tabular-nums">{row.received}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {open ? <b className="text-amber-700">{row.remaining}</b> : "–"}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right">
          <div className="flex justify-end gap-1">
            {sender && row.state === "OWNER_UNKNOWN" && (
              <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => setDialog({ kind: "assign", row })}>
                {t("inb.assign")}
              </Button>
            )}
            {sender && open && (
              <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => setDialog({ kind: "receive", row })}
                data-testid={`inbound-receive-${row.id}`}>
                {t("inb.receive")}
              </Button>
            )}
            {(tenant || sender) && row.state === "PARTIAL" && (
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setDialog({ kind: "close", row })}>
                {t("inb.close_short")}
              </Button>
            )}
            {row.receipt_count > 0 && (
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setDialog({ kind: "receipts", row })}>
                {t("inb.receipts_count", { count: row.receipt_count })}
              </Button>
            )}
            {tenant && open && (
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label={t("inb.edit_quantity")}
                onClick={() => setDialog({ kind: "item", row })}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {tenant && row.state === "ORDERED" && (
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                aria-label={t("common.delete")} onClick={() => setDialog({ kind: "delete", row })}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-3">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <div className="flex flex-wrap items-center gap-2">
        {warehouses.length > 1 && (
          <Select value={wid} onValueChange={v => { setWid(v); setTenantId("all"); }}>
            <SelectTrigger className="h-9 w-auto min-w-[10rem]"><SelectValue /></SelectTrigger>
            <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {!tenant && (
          <Select value={tenantId} onValueChange={setTenantId} disabled={unknownView}>
            <SelectTrigger className="h-9 w-auto min-w-[10rem]" data-testid={`${view}-tenant-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("planning.all_tenants")}</SelectItem>
              {(warehouse?.tenants || []).map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {view === "inbound" && (
          <div className="inline-flex flex-wrap rounded-lg bg-slate-100 p-1" role="group">
            {filters.map(f => (
              <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f}
                className={`rounded-md px-3 py-1 text-sm font-semibold ${filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                {t(`inb.filter.${f}`)}
              </button>
            ))}
          </div>
        )}
        {view === "inbound" && (
          <button type="button" onClick={() => setGrouped(g => !g)} aria-pressed={grouped}
            className={`flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm font-medium ${grouped
              ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
            <Layers className="h-3.5 w-3.5" /> {t("inb.group_by_order")}
          </button>
        )}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} className="h-9 pl-9" data-testid={`${view}-search`}
            placeholder={t(view === "stock" ? "inb.stock_search" : "inb.inbound_search")} />
        </div>
        <span className="flex-1" />
        <RefreshButton onClick={refresh} refreshing={refreshing} />
        {view === "inbound" && sender && (
          <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => setDialog({ kind: "unannounced" })}>
            <Plus className="h-4 w-4" /> {t("inb.unannounced")}
          </Button>
        )}
        {view === "inbound" && tenant && (<>
          <label className="cursor-pointer" title={t("inb.import_hint")}>
            <input type="file" className="sr-only" accept=".csv,.xlsx" disabled={importing}
              onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) importRows(f); }} />
            <span className="flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Upload className="h-3.5 w-3.5" /> {importing ? "…" : t("adm.import.button")}
            </span>
          </label>
          <Button size="sm" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => setDialog({ kind: "item" })}
            data-testid="inbound-add">
            <Plus className="h-4 w-4" /> {t("inb.add")}
          </Button>
        </>)}
      </div>

      {view === "stock" ? (
        <>
          <p className="text-xs text-slate-500">{t("inb.stock_legend")}</p>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  {!tenant && <th className="px-3 py-2">{t("planning.tenant")}</th>}
                  <th className="px-3 py-2">{t("common.product")}</th>
                  <th className="px-3 py-2 text-right">{t("inb.on_hand")}</th>
                  <th className="px-3 py-2 text-right">{t("inb.on_pallet")}</th>
                  <th className="px-3 py-2 text-right">{t("inb.on_shelf")}</th>
                  <th className="px-3 py-2 text-right">{t("inb.damaged")}</th>
                  <th className="px-3 py-2 text-right">{t("inb.on_the_way")}</th>
                </tr>
              </thead>
              <tbody>
                {shownStock.map(s => (
                  <tr key={`${s.tenant_id}-${s.asin}`} className="border-t border-slate-100">
                    {!tenant && <td className="px-3 py-2 text-xs font-semibold text-slate-600">{tenantName(s.tenant_id)}</td>}
                    <td className="px-3 py-2"><Product row={s} t={t} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.on_hand}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.on_pallet}</td>
                    <td className={`px-3 py-2 text-right text-base font-bold tabular-nums ${s.on_shelf < 0 ? "text-red-600" : ""}`}>{s.on_shelf}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.damaged ? <span className="font-semibold text-red-600">{s.damaged}</span> : "–"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.on_the_way || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!shownStock.length && (
              <p className="p-8 text-center text-sm text-slate-500">{t(stock.length ? "inb.search_no_match" : "inb.stock_empty")}</p>
            )}
          </div>
        </>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t("common.status")}</th>
                {!tenant && <th className="px-3 py-2">{t("planning.tenant")}</th>}
                <th className="px-3 py-2">{t("common.product")}</th>
                <th className="px-3 py-2">{t("inb.order_id")}</th>
                <th className="px-3 py-2 text-right">{t("inb.ordered")}</th>
                <th className="px-3 py-2 text-right">{t("inb.received")}</th>
                <th className="px-3 py-2 text-right">{t("inb.remaining")}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {grouped ? [...byOrder].flatMap(([orderId, list]) => [
                <tr key={`order-${orderId}`} className="border-t border-slate-200 bg-slate-100">
                  <td colSpan={tenant ? 7 : 8} className="px-3 py-1.5 text-xs font-semibold text-slate-700">
                    <span className="font-mono">{orderId || t("inb.no_order_id")}</span>
                    {" · "}{t("inb.group_summary", { count: list.length,
                      remaining: list.reduce((n, r) => n + (OPEN_STATES.includes(r.state) ? r.remaining : 0), 0) })}
                  </td>
                </tr>,
                ...list.map(renderRow),
              ]) : shownRows.map(renderRow)}
            </tbody>
          </table>
          {!shownRows.length && (
            <p className="p-8 text-center text-sm text-slate-500">{rows.length ? t("inb.search_no_match") : t(`inb.empty.${filter}`)}</p>
          )}
        </div>
      )}

      {dialog?.kind === "item" && (
        <ItemDialog row={dialog.row} warehouseId={wid} onClose={() => setDialog(null)}
          onSaved={() => done(t(dialog.row ? "inb.updated" : "inb.added"))} />
      )}
      {dialog?.kind === "receive" && (
        <ReceiveDialog row={dialog.row} onClose={() => setDialog(null)}
          onSaved={(receipt, files) => afterReceipt(receipt, files, t(receipt.damaged ? "inb.saved_damaged" : "inb.saved"))} />
      )}
      {dialog?.kind === "unannounced" && warehouse && (
        <UnannouncedDialog warehouse={warehouse} onClose={() => setDialog(null)}
          onSaved={(receipt, files, unknown) => { setFilter(unknown ? "unknown" : "done"); afterReceipt(receipt, files, t("inb.saved")); }} />
      )}
      {dialog?.kind === "assign" && (
        <AssignDialog row={dialog.row} tenants={warehouse?.tenants || []} onClose={() => setDialog(null)}
          onSaved={() => done(t("inb.assigned"))} />
      )}
      {dialog?.kind === "receipts" && (
        <ReceiptsDialog row={dialog.row} side={side} onClose={() => { setDialog(null); load(); }}
          onChanged={() => done(t("inb.acknowledged"))} />
      )}
      <ImportResultDialog result={importResult} onClose={() => setImportResult(null)}
        duplicatesKey="inb.import_duplicates" hintKey="inb.import_duplicates_hint" />
      <AlertDialog open={dialog?.kind === "close" || dialog?.kind === "delete"} onOpenChange={open => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialog?.kind === "close" ? t("inb.close_short") : t("common.delete")}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">
              {dialog?.kind === "close" ? t("inb.close_short_body", { count: dialog?.row?.remaining ?? 0 })
                : `${dialog?.row?.product_name || dialog?.row?.asin || ""} · ${dialog?.row?.order_id || ""}`}
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className={dialog?.kind === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
              onClick={() => dialog.kind === "close"
                ? act(() => axios.post(`${API}/inbound/items/${dialog.row.id}/close`, {}, ax), t("inb.closed"))
                : act(() => axios.delete(`${API}/inbound/items/${dialog.row.id}`, ax), t("inb.deleted"))}>
              {dialog?.kind === "close" ? t("inb.close_short") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
